import { useState, useEffect, useCallback, useRef } from 'react';
import { Search, Loader, AlertCircle, Check, Database, Package } from 'lucide-react';
import { useDebounce } from '../../hooks/useDebounce';
import { apogeeApiService } from '../../services/apogeeApi';
import { ApogeeSearchDisabledError, PackRow, SEARCH_PACKS_MAX_LIMIT } from '../../types/apogee';
import { packRowFromSearchHit } from '../../utils/packSearch';
import { cn } from '../../utils/cn';

const SEARCH_MIN_CHARS = 2;
const SEARCH_LIMIT = 15;

function filterPacksClientSide(items: PackRow[], q: string): PackRow[] {
  const n = q.toLowerCase().trim();
  if (!n) return [];
  return items.filter((p) => {
    const blob = `${p.name} ${p.slug} ${p.summary ?? ''}`.toLowerCase();
    return blob.includes(n);
  });
}

export interface ApogeePackSearchPickerProps {
  selected: PackRow | null;
  onSelect: (pack: PackRow | null) => void;
  disabled?: boolean;
  className?: string;
}

export function ApogeePackSearchPicker({
  selected,
  onSelect,
  disabled,
  className,
}: ApogeePackSearchPickerProps) {
  const [query, setQuery] = useState('');
  const debounced = useDebounce(query, 350);
  const [results, setResults] = useState<PackRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [searchDisabled, setSearchDisabled] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const runSearch = useCallback(
    async (q: string) => {
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      setLoading(true);
      setError(null);
      setNotice(null);

      try {
        if (searchDisabled) {
          const res = await apogeeApiService.listPacks(
            { limit: SEARCH_PACKS_MAX_LIMIT, offset: 0, sort: 'name' },
            ac.signal,
          );
          const items = res.items || [];
          setResults(filterPacksClientSide(items, q).slice(0, SEARCH_LIMIT));
          setNotice(
            'Pack search is unavailable on this deployment; results are filtered from a limited catalog slice. Prefer selecting a pack you can find by name.',
          );
          return;
        }

        try {
          const res = await apogeeApiService.searchPacks({ q, limit: SEARCH_LIMIT, offset: 0 }, ac.signal);
          const rows = (res.hits || [])
            .map((h) => packRowFromSearchHit(h))
            .filter((row): row is PackRow => row !== null);
          setResults(rows);
        } catch (err) {
          if (err instanceof ApogeeSearchDisabledError) {
            setSearchDisabled(true);
            const res = await apogeeApiService.listPacks(
              { limit: SEARCH_PACKS_MAX_LIMIT, offset: 0, sort: 'name' },
              ac.signal,
            );
            const items = res.items || [];
            setResults(filterPacksClientSide(items, q).slice(0, SEARCH_LIMIT));
            setNotice(`${err.message} Showing a limited name match instead.`);
            return;
          }
          throw err;
        }
      } catch (e) {
        if (e instanceof Error && e.name === 'AbortError') return;
        setError(e instanceof Error ? e.message : 'Search failed');
        setResults([]);
      } finally {
        if (abortRef.current === ac) {
          setLoading(false);
          abortRef.current = null;
        }
      }
    },
    [searchDisabled],
  );

  useEffect(() => {
    const q = debounced.trim();
    if (q.length < SEARCH_MIN_CHARS) {
      abortRef.current?.abort();
      setResults([]);
      setLoading(false);
      setError(null);
      setNotice(null);
      return;
    }
    void runSearch(q);
  }, [debounced, runSearch]);

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  return (
    <div className={cn('space-y-2', className)}>
      <label className="block text-xs font-medium text-glass-400 mb-1.5">
        Find pack (Apogee search) *
      </label>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400 pointer-events-none" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={disabled}
          autoComplete="off"
          className="glass-input w-full pl-9"
          placeholder="Type at least 2 characters (name, slug, platforms…)"
        />
        {loading && (
          <Loader className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-accent-400" />
        )}
      </div>
      <p className="text-xs text-glass-500">
        Results come from Apogee <code className="font-mono text-glass-400">GET /v1/search/packs</code>. Pick a row to
        link that catalog pack; the link stores the canonical name and Apogee ID when the API accepts it.
      </p>

      {error && (
        <div className="alert alert-danger text-xs">
          <AlertCircle />
          <span>{error}</span>
        </div>
      )}
      {notice && !error && (
        <div className="alert alert-warning text-xs">
          <AlertCircle />
          <span>{notice}</span>
        </div>
      )}

      {selected && (
        <div className="p-3 rounded-lg border border-accent-500/30 bg-accent-500/5 space-y-2">
          <div className="flex items-start gap-3">
            {selected.thumbnail_url ? (
              <img
                src={selected.thumbnail_url}
                alt=""
                className="w-12 h-12 rounded-md object-cover bg-glass-200/10 shrink-0"
              />
            ) : (
              <span className="icon-tile icon-tile-neutral w-12 h-12">
                <Package className="w-5 h-5" />
              </span>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-white text-sm">{selected.name}</span>
                <Check className="w-4 h-4 text-green-400 shrink-0" />
              </div>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-glass-500 mt-0.5 tabular-nums">
                <span className="inline-flex items-center gap-1">
                  <Database className="w-3 h-3" />
                  Apogee {selected.apogee_id}
                </span>
                {selected.mpi_id != null && <span>MPI {selected.mpi_id}</span>}
                {selected.cf_mod_id != null && <span>CF {selected.cf_mod_id}</span>}
                {selected.slug && <span className="truncate font-mono">{selected.slug}</span>}
              </div>
              {selected.summary && (
                <p className="text-xs text-glass-400 mt-1 line-clamp-2">{selected.summary}</p>
              )}
            </div>
          </div>
          <button
            type="button"
            disabled={disabled}
            onClick={() => onSelect(null)}
            className="text-xs text-accent-300 hover:text-accent-200 hover:underline underline-offset-2 touch-manipulation"
          >
            Clear selection
          </button>
        </div>
      )}

      {!selected && results.length > 0 && (
        <ul className="max-h-64 overflow-y-auto rounded-lg border border-glass-200/10 divide-y divide-glass-200/10">
          {results.map((pack) => (
            <li key={pack.apogee_id}>
              <button
                type="button"
                disabled={disabled}
                onClick={() => onSelect(pack)}
                className={cn(
                  'w-full text-left p-3 flex gap-3 hover:bg-glass-200/5 transition-colors touch-manipulation',
                  disabled && 'opacity-50 pointer-events-none',
                )}
              >
                {pack.thumbnail_url ? (
                  <img
                    src={pack.thumbnail_url}
                    alt=""
                    className="w-10 h-10 rounded-md object-cover bg-glass-200/10 shrink-0"
                  />
                ) : (
                  <span className="icon-tile icon-tile-neutral w-10 h-10">
                    <Package className="w-4 h-4" />
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-white truncate">{pack.name}</p>
                  <p className="text-[11px] text-glass-500 truncate tabular-nums">
                    Apogee {pack.apogee_id}
                    {pack.download_count != null ? ` · ${pack.download_count.toLocaleString()} dl` : ''}
                  </p>
                  {pack.summary && (
                    <p className="text-xs text-glass-400 line-clamp-2 mt-0.5">{pack.summary}</p>
                  )}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      {!selected && debounced.trim().length >= SEARCH_MIN_CHARS && !loading && results.length === 0 && !error && (
        <p className="text-xs text-glass-500 py-2">No packs match that search.</p>
      )}
    </div>
  );
}
